import type {
    MenuProps as _MenuProps,
    MenuButtonProps as _MenuButtonProps,
    MenuListProps as _MenuListProps,
    MenuItemProps as _MenuItemProps,
    MenuItemOptionProps as _MenuItemOptionProps,
    MenuGroupProps as _MenuGroupProps,
    MenuOptionGroupProps as _MenuOptionGroupProps,
    MenuDividerProps as _MenuDividerProps,
} from "@chakra-ui/react";
import {
    forwardRef,
    Menu as _Menu,
    MenuButton as _MenuButton,
    MenuItem as _MenuItem,
    MenuItemOption as _MenuItemOption,
    MenuGroup as _MenuGroup,
    MenuOptionGroup as _MenuOptionGroup,
    MenuDivider as _MenuDivider,
    MenuList as _MenuList,
    useMultiStyleConfig,
    useMenuContext,
    useMenuList,
} from "@chakra-ui/react";

import { Button } from "../../forms/button/Button";
import { ECCOIcon } from "../../media-and-icons/ecco-icon/ECCOIcon";
import { cx } from "@chakra-ui/utils";
import { scrollbarStyles } from "../../../css/01.scrollbar/scrollbar.styles";

export interface SelectDropdownProps extends _MenuProps {}

export interface SelectDropdownButtonProps extends _MenuButtonProps {
    /**
     * Define a custom color scheme for the button.
     */
    colorScheme?: string;
    isError?: boolean;
}

export interface SelectDropdownListProps extends _MenuListProps {
    /**
     * Define a max height of the list and make it scrollable.
     * @default undefined
     */
    scrollHeight?: number;
}

export interface SelectDropdownItemProps extends _MenuItemProps {}

export interface SelectDropdownItemOptionProps extends _MenuItemOptionProps {}

export interface SelectDropdownGroupProps extends _MenuGroupProps {}

export interface SelectDropdownOptionGroupProps extends _MenuOptionGroupProps {}

export interface SelectDropdownDividerProps extends _MenuDividerProps {}

export const SelectDropdown = ({
    children,
    isLazy = true,
    gutter = 0,
    autoSelect = true,
    ...props
}: SelectDropdownProps) => {
    return (
        <_Menu isLazy={isLazy} gutter={gutter} autoSelect={autoSelect} {...props}>
            {children}
        </_Menu>
    );
};

export const SelectDropdownButton = forwardRef<SelectDropdownButtonProps, "button">(
    ({ sx, isError, colorScheme = "gray", disabled, ...props }, ref) => {
        const styles = useMultiStyleConfig("SelectDropdown", props);
        const { isOpen } = useMenuContext();

        return (
            <_MenuButton
                ref={ref}
                as={Button}
                color={disabled ? "gray.600" : "black"}
                variant={"outline"}
                colorScheme={colorScheme}
                sx={{
                    ...styles.button,
                    ...sx,
                }}
                h={12}
                rightIcon={
                    <ECCOIcon
                        transitionProperty={"transform"}
                        transitionDuration={"200ms"}
                        transitionTimingFunction={"ease-out"}
                        transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                        name="down"
                        size={"2xs"}
                        willChange="transform"
                    />
                }
                {...props}
            />
        );
    }
);
export const SelectDropdownList = forwardRef<SelectDropdownListProps, "div">((props, ref) => {
    const { backdropFilter = "blur(10px)", children, scrollHeight, sx = {}, ...rest } = props;
    const ownProps = useMenuList(rest, ref) as any;
    const { isOpen } = useMenuContext();

    const combinedSx = {
        ...scrollbarStyles,
        ...sx,
    };
    return (
        <_MenuList
            {...ownProps}
            css={{
                backdropFilter: isOpen ? backdropFilter : "",
            }}
            overflowY="auto"
            maxHeight={scrollHeight}
            sx={combinedSx}
            shadow="lg"
            className={cx("chakra-menu__menu-list", props.className)}
        >
            {children}
        </_MenuList>
    );
});

export const SelectDropdownItem = forwardRef<SelectDropdownItemProps, "button">(
    // We're setting isFocusable to true by default to avoid issues with letter search and disabled items.
    ({ isFocusable = true, ...props }, ref) => {
        // Because we tend to use make the MenuList a scrollable component we have to manually scroll to the focused item.
        const onItemFocus = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
            props?.onFocus?.(e);
            e.target.scrollIntoView({
                block: "nearest",
                inline: "nearest",
                behavior: "auto",
            });
        };

        return <_MenuItem ref={ref} {...props} isFocusable={isFocusable} onFocus={onItemFocus} />;
    }
);

export const SelectDropdownItemOption = forwardRef<SelectDropdownItemOptionProps, "div">(
    ({ ...props }, ref) => {
        return <_MenuItemOption ref={ref} {...props} />;
    }
);

export const SelectDropdownGroup = forwardRef<SelectDropdownGroupProps, "div">(
    ({ ...props }, ref) => <_MenuGroup ref={ref} {...props} />
);

export const SelectDropdownOptionGroup: React.FC<SelectDropdownOptionGroupProps> = ({
    ...props
}) => <_MenuOptionGroup {...props} />;

export const SelectDropdownDivider: React.FC<SelectDropdownDividerProps> = ({ ...props }) => (
    <_MenuDivider {...props} />
);
